import { useCallback, useState } from 'react';
import { DriverDetailsFormItem } from '../components/manage-drivers/types';
import { IMidTermAdjustmentState } from '../store/midTermAdjustment.slice';
import { ICreateNewRenewalVersionResponse } from '../store/renewal.slice';
import { DriverFormValues } from '../utils/initialValues/get-driver-form-values';
import { DriverShortFormValues } from '../utils/initialValues/get-driver-short-form-values';
import { getDriverFromFullForm } from './helpers/get-driver-from-full-form';
import { getDriverFromShortForm } from './helpers/get-driver-from-short-form';

interface IUseDriverDetails {
	midTermAdjustment?: IMidTermAdjustmentState;
	renewalState?: ICreateNewRenewalVersionResponse;
}

export const useManageAdditionalDriverDetails = ({ midTermAdjustment, renewalState }: IUseDriverDetails) => {
	const [additionalDrivers, setAdditionalDrivers] = useState<DriverDetailsFormItem[]>();
	const [selectedItemIndex, setSelectedItem] = useState<number>();
	const [isDriverModalOpen, toggleDriverModal] = useState(false);
	const [showForm, toggleForm] = useState(false);
	const [selectedItem, setItem] = useState<DriverDetailsFormItem>();

	function reset() {
		toggleForm(false);
		setItem(undefined);
		setSelectedItem(undefined);
	}

	const getAdditionalDriversMta = useCallback(() => {
		if (!midTermAdjustment?.drivers) {
			return;
		}
		const updateDrivers: DriverDetailsFormItem[] = midTermAdjustment.drivers.map((driver) => ({
			...driver,
			isNewDriver: driver.relationshipToProposer !== 'P',
			otherEmployment: driver.otherEmploymentStatus ?? '',
		}));
		setAdditionalDrivers(updateDrivers);
	}, [midTermAdjustment?.drivers]);

	const getAdditionalDriversRenewal = useCallback(() => {
		if (!renewalState?.drivers) {
			return;
		}

		const updateDrivers: DriverDetailsFormItem[] = renewalState.drivers.map((driver) => ({
			...driver,
			isNewDriver: driver.relationshipToProposer !== 'P',
			otherEmployment: driver.otherEmploymentStatus ?? '',
		}));
		setAdditionalDrivers(updateDrivers);
	}, [renewalState?.drivers]);

	// Driver table action handler
	const onEditClick = (index: number) => {
		if (additionalDrivers) setItem(additionalDrivers[index]);
		setSelectedItem(index);
		toggleForm(true);
	};

	// Driver table action handler
	const onRemoveClick = (index: number) => {
		setSelectedItem(index);
		toggleDriverModal(true);
	};

	// Driver table action handler
	const onSetMainDriver = (index: number) => {
		if (!additionalDrivers) return;
		const newArray = additionalDrivers.map((driver, i) => ({
			...driver,
			isMainDriver: i === index,
		}));
		setAdditionalDrivers(newArray);
	};

	const onDelete = () => {
		if (!additionalDrivers) return;

		if (typeof selectedItemIndex === 'undefined') {
			toggleDriverModal(false);
			return;
		}
		let newArray = [...additionalDrivers];
		newArray.splice(selectedItemIndex, 1);
		if (newArray.length === 0) {
			toggleForm(true);
		}
		const selected = additionalDrivers[selectedItemIndex];

		if (selected.isMainDriver) {
			const proposer = newArray.find((x) => x.relationshipToProposer === 'P');
			if (proposer) {
				const newIndex = newArray.findIndex((d) => d === proposer);
				newArray = newArray.map((driver, i) => ({
					...driver,
					isMainDriver: i === newIndex,
				}));
			}
		}

		setAdditionalDrivers(newArray);
		toggleDriverModal(false);
		setSelectedItem(undefined);
	};

	const onFullFormSubmit = (formValues: DriverFormValues) => {
		const newDriver = getDriverFromFullForm(formValues);

		if (!additionalDrivers) return;
		const newArray = [...additionalDrivers];
		if (selectedItem && selectedItemIndex) {
			newArray[selectedItemIndex] = {
				...selectedItem,
				...newDriver,
			};
			setAdditionalDrivers(newArray);
			reset();
			return;
		}
		newArray.push(newDriver);
		setAdditionalDrivers(newArray);
		reset();
	};

	const onShortFormSubmit = (formValues: DriverShortFormValues, driverDetails?: DriverDetailsFormItem) => {
		const driver = getDriverFromShortForm(formValues, driverDetails);

		if (!additionalDrivers) return;
		const index = additionalDrivers.findIndex((d) => d.dataPointId === driver.dataPointId);
		const newArray = [...additionalDrivers];
		newArray[index] = {
			...selectedItem,
			...driver,
		};
		setAdditionalDrivers(newArray);
		reset();
	};

	return {
		additionalDrivers,
		setAdditionalDrivers,
		getAdditionalDriversMta,
		getAdditionalDriversRenewal,
		onRemoveClick,
		onSetMainDriver,
		onDelete,
		onEditClick,
		isDriverModalOpen,
		toggleDriverModal,
		showForm,
		toggleForm,
		selectedItem,
		setItem,
		setSelectedItem,
		selectedItemIndex,
		onFullFormSubmit,
		onShortFormSubmit,
	};
};
